import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  Tabs,
  Tab,
} from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import {
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Analytics as AnalyticsIcon,
  GetApp as ExportIcon,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import { useState, useCallback } from 'react'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { CourseIcon, UserIcon } from 'src/v3/core/icons'
import {
  selectAssignmentEventModal,
  setAssignmentEventOpen,
  selectAssignmentUserProgress,
  selectAssignmentCourseProgress,
  selectAssignmentProgressStats,
  selectAssignmentProgressMatrix,
} from '../../index'
import ProgressBar from '../ProgressBar'
import ProgressMatrix from '../ProgressMatrix'

export default function AssignmentEventModal() {
  let dispatch = useAppDispatch()
  let [activeTab, setActiveTab] = useState(0)

  let AssignmentEventToolbar = useCallback(
    () => <DataGridToolbar fileName="assignment-event" />,
    [],
  )

  // Redux selectors
  let assignmentEventModal = useAppSelector(selectAssignmentEventModal)
  let { selectedAssignmentEvent } = assignmentEventModal

  // Get progress data using the assignment ID
  let userProgress = useAppSelector((state) =>
    selectedAssignmentEvent
      ? selectAssignmentUserProgress(
          state,
          selectedAssignmentEvent.assignmentId,
        )
      : [],
  )
  let courseProgress = useAppSelector((state) =>
    selectedAssignmentEvent
      ? selectAssignmentCourseProgress(
          state,
          selectedAssignmentEvent.assignmentId,
        )
      : [],
  )
  let progressStats = useAppSelector((state) =>
    selectedAssignmentEvent
      ? selectAssignmentProgressStats(
          state,
          selectedAssignmentEvent.assignmentId,
        )
      : null,
  )
  let progressMatrix = useAppSelector((state) =>
    selectedAssignmentEvent
      ? selectAssignmentProgressMatrix(
          state,
          selectedAssignmentEvent.assignmentId,
        )
      : null,
  )

  let handleClose = () => {
    dispatch(setAssignmentEventOpen(false))
  }

  let handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  if (!assignmentEventModal.assignmentEventOpen || !selectedAssignmentEvent) {
    return null
  }

  // Calculate assignment timeline
  let createdDate = dayjs(selectedAssignmentEvent.createdDate)
  let dueDate = dayjs(selectedAssignmentEvent.dueDate)
  let daysUntilDue = dueDate.diff(dayjs(), 'day')
  let assignmentDuration = dueDate.diff(createdDate, 'day')

  // Define columns for user table - ONLY USEFUL COLUMNS!
  let userColumns: GridColDef[] = [
    { field: 'userName', headerName: 'Name', flex: 1 },
    { field: 'userEmail', headerName: 'Email', flex: 1 },
    {
      field: 'completedCourses',
      headerName: 'Completed',
      type: 'number',
      flex: 1,
    },
    { field: 'totalCourses', headerName: 'Total', type: 'number', flex: 1 },
    {
      field: 'completionPercentage',
      headerName: 'Progress %',
      type: 'number',
      flex: 1,
    },
    { field: 'lastActivityDate', headerName: 'Last Activity', flex: 1 },
  ]

  // Define columns for course table - ONLY USEFUL COLUMNS!
  let courseColumns: GridColDef[] = [
    { field: 'courseTitle', headerName: 'Course Title', flex: 1 },
    {
      field: 'completedByUsers',
      headerName: 'Completed By',
      type: 'number',
      flex: 1,
    },
    {
      field: 'totalAssignedUsers',
      headerName: 'Total Users',
      type: 'number',
      flex: 1,
    },
    {
      field: 'completionPercentage',
      headerName: 'Completion %',
      type: 'number',
      flex: 1,
    },
  ]

  let userTableData = userProgress || []
  let courseTableData = courseProgress || []

  return (
    <Dialog
      open={assignmentEventModal.assignmentEventOpen}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          sx: { bgcolor: 'background.paper' },
        },
      }}
      maxWidth="lg"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Box>
          <Typography
            color="text.primary"
            variant="h6"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AssignmentIcon />
            Assignment Details
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Created on {createdDate.format('MMMM DD, YYYY')}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: 'text.primary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: 'background.paper', p: 0 }}>
        {/* Assignment Overview Header */}
        <Box sx={{ p: 3, pb: 0 }}>
          <Typography
            color="text.primary"
            variant="h6"
            sx={{
              mb: 2,
            }}
          >
            {selectedAssignmentEvent.assignmentName}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Chip
              icon={<UserIcon />}
              label={`${selectedAssignmentEvent.userCount} Users`}
              color="primary"
              variant="filled"
            />
            <Chip
              icon={<CourseIcon />}
              label={`${selectedAssignmentEvent.courseCount} Courses`}
              color="secondary"
              variant="filled"
            />
            <Chip
              label={`${assignmentDuration} Day Duration`}
              color="info"
              variant="filled"
            />
            {progressStats && (
              <Chip
                label={`${progressStats.overallCompletionPercentage}% Complete`}
                color={
                  progressStats.overallCompletionPercentage >= 80
                    ? 'success'
                    : 'warning'
                }
                variant="filled"
              />
            )}
          </Box>

          {/* Overall Progress Bar */}
          {progressStats && (
            <Box sx={{ mb: 3 }}>
              <ProgressBar
                value={progressStats.overallCompletionPercentage}
                label="Overall Assignment Progress"
                showPercentage
                showFraction={false}
                color={
                  progressStats.overallCompletionPercentage >= 80
                    ? 'success'
                    : 'warning'
                }
              />
            </Box>
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': { color: 'text.primary' },
              '& .MuiTab-root.Mui-selected': { color: 'primary.main' },
              '& .MuiTabs-indicator': { backgroundColor: 'primary.main' },
            }}
          >
            <Tab
              icon={<AssignmentIcon />}
              label="Overview"
              iconPosition="start"
              sx={{ color: 'text.primary' }}
            />
            <Tab
              icon={<PeopleIcon />}
              label={`Users (${userProgress.length})`}
              iconPosition="start"
              sx={{ color: 'text.primary' }}
            />
            <Tab
              icon={<SchoolIcon />}
              label={`Courses (${courseProgress.length})`}
              iconPosition="start"
              sx={{ color: 'text.primary' }}
            />
            <Tab
              icon={<AnalyticsIcon />}
              label="Progress Matrix"
              iconPosition="start"
              sx={{ color: 'text.primary' }}
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ p: 3 }}>
          {/* Overview Tab */}
          {activeTab === 0 && (
            <Box>
              {/* Timeline Information */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  color="text.primary"
                  variant="subtitle1"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Assignment Timeline
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography color="text.primary" variant="body2">
                      <strong>Created:</strong>{' '}
                      {createdDate.format('MMM DD, YYYY')} (
                      {createdDate.fromNow()})
                    </Typography>
                    <Chip
                      size="small"
                      label="Assignment Created"
                      color="info"
                      variant="filled"
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography color="text.primary" variant="body2">
                      <strong>Due Date:</strong>{' '}
                      {dueDate.format('MMM DD, YYYY')} ({dueDate.fromNow()})
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        daysUntilDue > 0
                          ? `${daysUntilDue} days remaining`
                          : daysUntilDue === 0
                            ? 'Due today'
                            : `${Math.abs(daysUntilDue)} days overdue`
                      }
                      color={
                        daysUntilDue > 7
                          ? 'success'
                          : daysUntilDue > 0
                            ? 'warning'
                            : 'error'
                      }
                      variant="filled"
                    />
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ mb: 3, bgcolor: 'divider' }} />

              {/* Assignment Statistics */}
              {progressStats && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    color="text.primary"
                    variant="subtitle1"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                    }}
                  >
                    Assignment Statistics
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Total Assigned Users
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {progressStats.totalUsers}
                      </Typography>
                    </Box>

                    <Box
                      sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Required Courses
                      </Typography>
                      <Typography variant="h5" color="secondary">
                        {progressStats.totalCourses}
                      </Typography>
                    </Box>

                    <Box
                      sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Fully Completed Users
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        {progressStats.fullyCompletedUsers}
                      </Typography>
                    </Box>

                    <Box
                      sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Overall Progress
                      </Typography>
                      <Typography variant="h5" sx={{ color: 'info.main' }}>
                        {progressStats.overallCompletionPercentage}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Users Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography
                color="text.primary"
                variant="h6"
                sx={{
                  mb: 2,
                }}
              >
                User Progress ({userProgress.length} users)
              </Typography>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGridPremium
                  rows={userTableData}
                  columns={userColumns}
                  slots={{ toolbar: AssignmentEventToolbar }}
                  showToolbar
                  getRowId={(row) => row.userId}
                />
              </Box>
            </Box>
          )}

          {/* Courses Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography
                color="text.primary"
                variant="h6"
                sx={{
                  mb: 2,
                }}
              >
                Course Progress ({courseProgress.length} courses)
              </Typography>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGridPremium
                  rows={courseTableData}
                  columns={courseColumns}
                  slots={{ toolbar: AssignmentEventToolbar }}
                  showToolbar
                  getRowId={(row) => row.courseId}
                />
              </Box>
            </Box>
          )}

          {/* Progress Matrix Tab */}
          {activeTab === 3 && (
            <Box>
              <Typography
                color="text.primary"
                variant="h6"
                sx={{
                  mb: 2,
                }}
              >
                Detailed Progress Matrix
              </Typography>
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{
                  mb: 2,
                }}
              >
                This view shows which users have completed which courses within
                this assignment with completion dates.
              </Typography>

              <ProgressMatrix matrixData={progressMatrix} height={450} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'background.paper', px: 3, py: 2 }}>
        <Button
          startIcon={<ExportIcon />}
          variant="outlined"
          sx={{ color: 'text.primary', borderColor: 'text.primary' }}
        >
          Export Data
        </Button>
        <Button onClick={handleClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
